import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import ConfirmModal from '../ui/ConfirmModal';
import { retryDocument } from '../../services/api';
import { promptService } from '../../services/promptService';
import { schemaService } from '../../services/schemaService';
import type { Prompt } from '../../types';
import type { ExtractionSchema } from '../../types';

interface Props {
  open: boolean;
  documentId: string;
  domain: string;
  validatedAt?: string | null;
  validatedByName?: string | null;
  onCancel: () => void;
  onConfirmed: () => void;
}

const ACTIVE_VALUE = '__active__';

const ReextractModal: React.FC<Props> = ({
  open,
  documentId,
  domain,
  validatedAt,
  validatedByName,
  onCancel,
  onConfirmed,
}) => {
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [schemas, setSchemas] = useState<ExtractionSchema[]>([]);
  const [promptId, setPromptId] = useState<string>(ACTIVE_VALUE);
  const [schemaId, setSchemaId] = useState<string>(ACTIVE_VALUE);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPromptId(ACTIVE_VALUE);
    setSchemaId(ACTIVE_VALUE);
    promptService.list({ domain }).then(setPrompts).catch(() => setPrompts([]));
    schemaService.list({ domain }).then(setSchemas).catch(() => setSchemas([]));
  }, [open, domain]);

  const handleConfirm = async () => {
    if (submitting) return;
    setSubmitting(true);
    const body: { prompt_id?: string; schema_id?: string } = {};
    if (promptId !== ACTIVE_VALUE) body.prompt_id = promptId;
    if (schemaId !== ACTIVE_VALUE) body.schema_id = schemaId;
    try {
      await retryDocument(documentId, body);
      toast.success('Réextraction lancée');
      onConfirmed();
    } catch (err: any) {
      toast.error(err?.message || 'Erreur lors de la réextraction');
    } finally {
      setSubmitting(false);
    }
  };

  const validatedDate = validatedAt
    ? new Date(validatedAt).toLocaleDateString('fr-CA')
    : null;

  const message = (
    <div className="space-y-4">
      <p>
        {validatedDate && validatedByName
          ? `Le document a été validé le ${validatedDate} par ${validatedByName}. `
          : 'Ce document a une extraction courante. '}
        La réextraction créera une nouvelle version (v+1) et la validation actuelle
        sera conservée en historique mais devra être refaite.
      </p>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Prompt à utiliser
        </label>
        <select
          value={promptId}
          onChange={(e) => setPromptId(e.target.value)}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          disabled={submitting}
        >
          <option value={ACTIVE_VALUE}>Config active</option>
          {prompts.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name} (v{p.version})
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Schéma à utiliser
        </label>
        <select
          value={schemaId}
          onChange={(e) => setSchemaId(e.target.value)}
          className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
          disabled={submitting}
        >
          <option value={ACTIVE_VALUE}>Config active</option>
          {schemas.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name} (v{s.version})
            </option>
          ))}
        </select>
      </div>
    </div>
  );

  return (
    <ConfirmModal
      open={open}
      title="Ré-extraire le document"
      message={message}
      confirmLabel={submitting ? 'En cours...' : 'Confirmer'}
      cancelLabel="Annuler"
      onCancel={onCancel}
      onConfirm={handleConfirm}
    />
  );
};

export default ReextractModal;
